import api from './api';

const formService = {
  // Get all form types
  getFormTypes: async () => {
    try {
      const response = await api.get('/api/forms/types');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get all equipment types or filter by form_type_id
  getEquipmentTypes: async (formTypeId = null) => {
    try {
      const url = formTypeId
        ? `/api/forms/equipment-types?form_type_id=${formTypeId}`
        : '/api/forms/equipment-types';

      const response = await api.get(url);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get template by form type and equipment type
  getTemplate: async (formTypeId, equipmentTypeId) => {
    try {
      const response = await api.get('/api/forms/template', {
        params: {
          form_type_id: formTypeId,
          equipment_type_id: equipmentTypeId
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get template fields
  getTemplateFields: async (templateId) => {
    try {
      const response = await api.get(`/api/forms/template/${templateId}/fields`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get all clients
  getClients: async () => {
    try {
      const response = await api.get('/api/forms/clients');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Submit form
  submitForm: async (formData) => {
    try {
      const response = await api.post('/api/forms/submit', formData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get all submissions for current user (role-based)
  getSubmissions: async () => {
    try {
      const response = await api.get('/api/forms/submissions');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get submission details
  getSubmissionDetails: async (submissionId) => {
    try {
      const response = await api.get(`/api/forms/submissions/${submissionId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Senior inspector review submission
  reviewSubmission: async (submissionId, action, comments) => {
    try {
      const response = await api.put(`/api/forms/submissions/${submissionId}/review`, {
        action,
        comments
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Admin approve submission
  approveSubmission: async (submissionId, clientEmail) => {
    try {
      const response = await api.put(`/api/forms/submissions/${submissionId}/approve`, {
        client_email: clientEmail
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Copy form data from a previous work order
  copyFromWorkOrder: async (workOrderNumber) => {
    try {
      const response = await api.get('/api/forms/copy-from-workorder', {
        params: { work_order_number: workOrderNumber }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Update submission (for sent-back forms)
  updateSubmission: async (submissionData) => {
    try {
      const response = await api.put(`/api/forms/submissions/${submissionData.submission_id}/update`, submissionData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get equipment types filtered by form_type_code (ADN / ENA)
  getEquipmentTypesByCode: async (formTypeCode) => {
    try {
      const response = await api.get(`/api/forms/equipment-types?form_type_code=${formTypeCode}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Create or update a payment record
  upsertPayment: async (paymentData) => {
    try {
      const response = await api.post('/api/forms/payment', paymentData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Get all active clients for Email Certificates dropdown
  getClientsList: async () => {
    try {
      const response = await api.get('/api/forms/clients-list');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Search approved certificates by work order number
  getCertificatesByWorkOrder: async (workOrderNo) => {
    try {
      const response = await api.get(`/api/forms/certificates-by-workorder?work_order_no=${encodeURIComponent(workOrderNo)}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Email selected certificates to a client
  emailCertificatesToClient: async (submissionIds, clientId) => {
    try {
      const response = await api.post('/api/forms/email-certificates-to-client', {
        submission_ids: submissionIds,
        client_id: clientId,
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Upload an image for a 'file' type form field (e.g. inspection photo).
  // Returns { success, filename } — store the filename as the field's value,
  // same as any other field's text value.
  uploadFieldFile: async (file) => {
    try {
      const data = new FormData();
      data.append('file', file);
      const response = await api.post('/api/forms/upload-field-file', data, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  },

  // Fetch a previously uploaded field file's image data (for preview thumbnails)
  getFieldFile: async (filename) => {
    const response = await api.get(`/api/forms/field-file/${encodeURIComponent(filename)}`, {
      responseType: 'blob'
    });
    return response.data;
  },
};

export default formService;
